import * as React from 'react';
import './MessagesMenu.scss';
import ChatIcon from '@material-ui/icons/Chat';
import EmailIcon from '@material-ui/icons/Email';
import SmsIcon from '@material-ui/icons/Sms';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import { DropdownItem, Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import { IconButton, Badge, Tooltip } from '@material-ui/core';
import { IncomingMessageNotification, CommunicationType } from '../../../models';
import moment from 'moment';
import 'moment/locale/pt';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getLanguage } from '../../../store/translations/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { isAdministrator } from '../../../store/session/selectors';
import { getCommunicationQuotas } from '../../../store/general/selectors';
import { loadCommunicationQuotasIfNeeded } from '../../../store/general/actions';

export interface MessagesMenuStateProps {
    unreadMessages: IncomingMessageNotification[],
    recentMessages: IncomingMessageNotification[],
}

export interface MessagesMenuDispatchProps {
    onMessageClick: (message: IncomingMessageNotification) => void,
    onMarkAllAsRead: () => Promise<void>,
}

export type MessagesMenuProps = MessagesMenuStateProps & MessagesMenuDispatchProps;

const MessagesMenu: React.FC<MessagesMenuProps> = (props) => {
    const {
        unreadMessages = [],
        recentMessages = [],
        onMessageClick,
        onMarkAllAsRead,
    } = props;

    const [isOpen, setOpen] = React.useState(false);
    const dispatch = useDispatch();
    const [isMarkingAllAsRead, setIsMarkingAllAsRead] = React.useState(false);
    const t = useTranslations();
    const isAdmin = useSelector(isAdministrator);
    const communicationQuotas = useSelector(getCommunicationQuotas) ?? null;

    React.useEffect(() => {
        if (!isAdmin) {
            return;
        }

        if (!isOpen || communicationQuotas) {
            return;
        }

        dispatch(loadCommunicationQuotasIfNeeded());
    }, [communicationQuotas, dispatch, isAdmin, isOpen]);

    const formatTimestamp = (timestamp: string) => {
        const lang = getLanguage();
        return moment(timestamp).locale(lang).format('MMM D, HH:mm');
    };

    const getMessageIcon = (type: CommunicationType) => {
        if (type === CommunicationType.Email) return <EmailIcon fontSize="small" />;
        if (type === CommunicationType.WhatsApp) return <WhatsAppIcon fontSize="small" />;
        return <SmsIcon fontSize="small" />;
    };

    const getMessageChannelLabel = (type: CommunicationType) => {
        if (type === CommunicationType.Email) return t(TK.email);
        if (type === CommunicationType.WhatsApp) return t(TK.whatsApp);
        return t(TK.sms);
    };

    const truncateText = (text: string | undefined | null, maxLength: number = 80) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    const getMessageDescription = (message: IncomingMessageNotification) => {
        const clientName = message.clientName || t(TK.client);
        const preview = truncateText(message.textPreview);
        return preview ? `${clientName} - ${preview}` : clientName;
    };

    const renderMessageRow = (message: IncomingMessageNotification) => (
        <DropdownItem
            key={message.id}
            className="messageItem"
            onClick={() => {
                setOpen(false);
                onMessageClick(message);
            }}
        >
            <span className="messageTypeIcon">
                {getMessageIcon(message.type)}
            </span>
            <div className="messageBody">
                <div className="messageMetaRow">
                    <span className="messageChannelBadge">{getMessageChannelLabel(message.type)}</span>
                    <span className="messageTimestamp">{formatTimestamp(message.timestamp)}</span>
                </div>
                <div className="messageDescription">{getMessageDescription(message)}</div>
            </div>
        </DropdownItem>
    );

    const usageRatio = (used: number, allowed: number) => {
        if (!allowed || allowed <= 0) return 0;
        return used / allowed;
    };

    const getRemainingQuotaLabel = (type: CommunicationType, remaining: number) => {
        if (type === CommunicationType.Email) {
            if (remaining <= 0) return t(TK.youHaveNoEmailsLeft);
            if (remaining === 1) return t(TK.youOnlyHaveOneEmailLeft);
            return t(TK.youOnlyHaveEmailsLeft, String(remaining));
        }

        if (type === CommunicationType.WhatsApp) {
            if (remaining <= 0) return t(TK.youHaveNoWhatsAppsLeft);
            if (remaining === 1) return t(TK.youOnlyHaveOneWhatsAppLeft);
            return t(TK.youOnlyHaveWhatsAppsLeft, String(remaining));
        }

        if (remaining <= 0) return t(TK.youHaveNoSmsLeft);
        if (remaining === 1) return t(TK.youOnlyHaveOneSmsLeft);
        return t(TK.youOnlyHaveSmsLeft, String(remaining));
    };

    const quotaIndicator = React.useMemo(() => {
        if (!communicationQuotas) return null;

        const smsRatio = usageRatio(communicationQuotas.smsUsed, communicationQuotas.smsAllowed);
        const whatsAppRatio = usageRatio(communicationQuotas.whatsAppUsed, communicationQuotas.whatsAppAllowed);
        const maxRatio = Math.max(smsRatio, whatsAppRatio);

        if (maxRatio >= 1) return 'critical';
        if (maxRatio >= 0.95) return 'high';
        if (maxRatio >= 0.85) return 'warning';
        return null;
    }, [communicationQuotas]);

    const quotaTooltip = React.useMemo(() => {
        if (!communicationQuotas || !quotaIndicator) return null;

        const lang = getLanguage();

        const channels = [
            { type: CommunicationType.SMS, used: communicationQuotas.smsUsed, allowed: communicationQuotas.smsAllowed, exhaustedOn: communicationQuotas.smsQuotaExhaustedOn },
            { type: CommunicationType.WhatsApp, used: communicationQuotas.whatsAppUsed, allowed: communicationQuotas.whatsAppAllowed, exhaustedOn: communicationQuotas.whatsAppQuotaExhaustedOn },
        ].filter(c => c.allowed > 0 && usageRatio(c.used, c.allowed) >= 0.85);

        if (!channels.length) return null;

        return (
            <div className="quotaTooltip">
                {channels.map(c => (
                    <div key={c.type}>
                        <b>{getRemainingQuotaLabel(c.type, Math.max(c.allowed - c.used, 0))}</b>
                        {c.exhaustedOn && (
                            <span> — {t(TK.quotaReachedOn)} {moment(c.exhaustedOn).locale(lang).format('LL')}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    }, [communicationQuotas, quotaIndicator, t]);

    const chatButton = (
        <IconButton className="messagesIconButton">
            <Badge badgeContent={unreadMessages.length} color="secondary">
                <ChatIcon style={{ color: 'white' }} />
            </Badge>
            {quotaIndicator && (
                <span className={`quotaIndicator quotaIndicator--${quotaIndicator}`} />
            )}
        </IconButton>
    );

    return (
        <Dropdown className="messagesMenuContainer" isOpen={isOpen} toggle={() => setOpen(!isOpen)}>
            <DropdownToggle nav className="messagesButton">
                {quotaTooltip ? (
                    <Tooltip title={quotaTooltip} arrow>
                        <span>{chatButton}</span>
                    </Tooltip>
                ) : chatButton}
            </DropdownToggle>

            <DropdownMenu className="messagesList" right>
                <div className="messagesListBody">
                    <div className="messagesSectionHeader">{t(TK.unreadMessages)}</div>
                    {unreadMessages.length === 0
                        ? <DropdownItem disabled className="messageItem messageItem--empty">{t(TK.noUnreadMessages)}</DropdownItem>
                        : unreadMessages.map(renderMessageRow)}
                    <DropdownItem divider className="messagesSectionDivider" />
                    <div className="messagesSectionHeader">{t(TK.recentMessages)}</div>
                    {recentMessages.length === 0
                        ? <DropdownItem disabled className="messageItem messageItem--empty">{t(TK.noRecentMessages)}</DropdownItem>
                        : recentMessages.map(renderMessageRow)}
                </div>
                <div className="messagesListFooter">
                    <DropdownItem
                        className="messagesFooterButton"
                        disabled={unreadMessages.length === 0 || isMarkingAllAsRead}
                        onClick={async () => {
                            if (isMarkingAllAsRead) return;
                            setIsMarkingAllAsRead(true);
                            setOpen(false);
                            try {
                                await onMarkAllAsRead();
                            } finally {
                                setIsMarkingAllAsRead(false);
                            }
                        }}
                    >
                        {t(TK.markAllAsRead)}
                    </DropdownItem>
                </div>
            </DropdownMenu>
        </Dropdown>
    )
}

export default MessagesMenu;
